import { useQuery } from '@tanstack/react-query';
import { LinearCanonicalNetworkEditedRow } from '@blisspointmedia/bpm-types/dist/LinearAssignments';
import {
  GetSalesAndTrafficContactsResponse,
  UpdateSalesAndTrafficContactsParams
} from '@blisspointmedia/bpm-types/dist/SalesAndTrafficContacts';
import {
  awaitJSON,
  LinearBuyingLambdaFetch,
  LinearLambdaFetch,
  StreamingOrdersLambdaFetch
} from '@src/utils/fetch-utils';
import { RatesRow } from './linear-networks-constants';

export const useCanonicalNetworks = (): ReturnType<
  typeof useQuery<LinearCanonicalNetworkEditedRow[]>
> =>
  useQuery({
    queryKey: ['linearCanonicalNetworks'],
    queryFn: async (): Promise<LinearCanonicalNetworkEditedRow[]> =>
      awaitJSON<LinearCanonicalNetworkEditedRow[]>(
        await LinearBuyingLambdaFetch('/linear_canonical_networks')
      )
  });

export const useRates = (): ReturnType<typeof useQuery<RatesRow[]>> =>
  useQuery({
    queryKey: ['linearRates'],
    queryFn: async (): Promise<RatesRow[]> =>
      awaitJSON<RatesRow[]>(await LinearLambdaFetch('/get_rates'))
  });

export const useContacts = (): ReturnType<typeof useQuery<GetSalesAndTrafficContactsResponse>> =>
  useQuery({
    queryKey: ['linearContacts'],
    queryFn: async (): Promise<GetSalesAndTrafficContactsResponse> =>
      awaitJSON<GetSalesAndTrafficContactsResponse>(
        await LinearBuyingLambdaFetch('/sales_and_traffic_contacts')
      )
  });

// NATIONAL + DMA names, for the rates Market dropdown.
export const useMarketOptions = (): ReturnType<typeof useQuery<string[]>> =>
  useQuery({
    queryKey: ['dmaMarketOptions'],
    queryFn: async (): Promise<string[]> => {
      const dmaMap = await awaitJSON<Record<string, string>>(
        await StreamingOrdersLambdaFetch('/getDMAMap')
      );
      return ['NATIONAL', ...Object.values(dmaMap).sort()];
    }
  });

// ---- save helpers (imperative POST; caller refetches the relevant query on success) ----

export const saveNetworks = async (networks: LinearCanonicalNetworkEditedRow[]): Promise<void> => {
  await LinearBuyingLambdaFetch('/linear_canonical_networks', {
    method: 'POST',
    body: networks
  });
};

export interface RateChanges {
  added: Record<string, RatesRow>;
  removed: Record<string, RatesRow>;
  updated: Record<string, RatesRow>;
}

export const saveRates = async (changes: RateChanges, email: string): Promise<void> => {
  await LinearLambdaFetch('/set_rates', {
    method: 'POST',
    body: { changes, email }
  });
};

export const saveContacts = async (params: UpdateSalesAndTrafficContactsParams): Promise<void> => {
  await LinearBuyingLambdaFetch('/sales_and_traffic_contacts', {
    method: 'POST',
    body: params
  });
};
